import type { World } from './world';
import type { EntityId, MobEnt, PlayerEnt, DraftOffer, ConsumableStack, Equipment } from './types';
import { defOf } from './mobs';
import { SKILLS, resolveSkill } from '../content/skills';
import type { HeroId } from '../content/heroes';
import type { ItemInst } from '../content/items';
import { freePoints } from './build';

export interface UnitView {
  id: number;
  /** 0 player, 1 mob, 2 boss, 3 revenant, 4 summon */
  k: number;
  x: number;
  y: number;
  hp: number;
  mhp: number;
  f: number;
  t: number;
  sh: number;
  /** status ids (short) */
  st?: string[];
  /** static fields, present only when first seen */
  n?: string;
  d?: string;
  h?: HeroId;
  el?: boolean;
  af?: string[];
  lv?: number;
  sz?: number;
  w?: string;
  ow?: number;
  /** account pet */
  pt?: boolean;
  /** windup progress 0..1 while telegraphing */
  wu?: number;
}

export interface ZoneView {
  id: number;
  x: number;
  y: number;
  r: number;
  v: string;
  t: number;
  until: number;
}

export interface PickupView {
  id: number;
  x: number;
  y: number;
  ty: string;
  def?: string;
  rar?: number;
}

export interface InterView {
  id: number;
  x: number;
  y: number;
  ty: string;
  used: boolean;
}

export interface SkillView {
  id: string;
  level: number;
  aspects: string[];
  auto?: boolean;
  /** cooldown fraction remaining 0..1 */
  cd: number;
}

export interface SelfView {
  id: number;
  hero: HeroId;
  x: number;
  y: number;
  seq: number;
  hp: number;
  mhp: number;
  sh: number;
  xp: number;
  xpNext: number;
  level: number;
  power: number;
  rank: number;
  rankXp: number;
  rankXpNext: number;
  /** unspent rank points */
  pts: number;
  actives: SkillView[];
  autos: SkillView[];
  traits: { id: string; level: number }[];
  keystones: string[];
  bless: string[];
  eq: Equipment;
  bp: ItemInst[];
  cons: (ConsumableStack | null)[];
  draft: DraftOffer | null;
  dash: { charges: number; max: number; cd: number };
  st: { id: string; stacks: number; left: number }[];
  kills: number;
  fame: number;
  dead: boolean;
  stats: { armor: number; dmg: number; spd: number; crit: number; aspd: number; cdr: number; area: number; thorns: number; ls: number; luck: number; regen: number };
  flags: string[];
  pet?: { id: string; level: number };
}

export interface Snapshot {
  tick: number;
  me?: SelfView;
  u: UnitView[];
  gone: number[];
  z: ZoneView[];
  pk: PickupView[];
  it: InterView[];
}

const KIND_CODE: Record<string, number> = { player: 0, mob: 1, boss: 2, revenant: 3, summon: 4 };

export function buildSnapshot(world: World, viewer: PlayerEnt | null, known: Set<EntityId>, cx: number, cy: number, radius: number): Snapshot {
  const r2 = radius * radius;
  const seen = new Set<EntityId>();
  const units: UnitView[] = [];
  const push = (u: PlayerEnt | MobEnt) => {
    const first = !known.has(u.id);
    const v: UnitView = {
      id: u.id,
      k: KIND_CODE[u.kind] ?? 1,
      x: Math.round(u.x * 10) / 10,
      y: Math.round(u.y * 10) / 10,
      hp: Math.round(u.hp),
      mhp: Math.round(u.maxHp),
      f: Math.round(u.facing * 100) / 100,
      t: u.team,
      sh: Math.round(u.shield),
    };
    if (u.statuses.length) v.st = u.statuses.map((s) => s.id);
    if (u.kind === 'player') {
      const p = u as PlayerEnt;
      if (first) {
        v.n = p.name;
        v.h = p.heroId;
      }
      v.lv = p.level;
      v.w = p.equipment.weapon.def;
    } else {
      const m = u as MobEnt;
      if (first) {
        v.d = m.def;
        v.el = m.elite;
        if (m.affixes.length) v.af = m.affixes;
        v.sz = defOf(m.def).size;
        if (m.kind === 'revenant') {
          v.n = m.name;
          v.h = m.ghost?.heroId;
          v.lv = m.level;
        } else if (m.name && m.name !== m.def) v.n = m.name;
        if (m.owner) v.ow = m.owner;
        if (m.isPet) v.pt = true;
      }
      if (m.windup) {
        const total = Math.max(1, m.windup.until - (m.atkCd.get(m.windup.attack) ?? m.windup.until));
        v.wu = Math.min(1, Math.max(0, 1 - (m.windup.until - world.tick) / Math.max(1, Math.min(total, 40))));
      }
    }
    units.push(v);
    seen.add(u.id);
  };
  for (const p of world.players.values()) {
    if (p.dead && world.tick > p.respawnAt + 40) continue;
    const dx = p.x - cx;
    const dy = p.y - cy;
    if (dx * dx + dy * dy <= r2 || p === viewer) push(p);
  }
  for (const m of world.mobs.values()) {
    if (m.dead) continue;
    const dx = m.x - cx;
    const dy = m.y - cy;
    if (dx * dx + dy * dy <= r2) push(m);
  }
  const gone: number[] = [];
  for (const id of known) if (!seen.has(id)) gone.push(id);
  for (const id of gone) known.delete(id);
  for (const id of seen) known.add(id);

  const z: ZoneView[] = [];
  for (const zn of world.zones.values()) {
    const dx = zn.x - cx;
    const dy = zn.y - cy;
    if (dx * dx + dy * dy <= (radius + zn.r) * (radius + zn.r)) z.push({ id: zn.id, x: Math.round(zn.x), y: Math.round(zn.y), r: Math.round(zn.r), v: zn.visual, t: zn.team, until: zn.until });
  }
  const pk: PickupView[] = [];
  for (const p of world.pickups.values()) {
    if (p.owner && (!viewer || p.owner !== viewer.id)) continue;
    const dx = p.x - cx;
    const dy = p.y - cy;
    if (dx * dx + dy * dy > r2) continue;
    pk.push({ id: p.id, x: Math.round(p.x), y: Math.round(p.y), ty: p.type, def: p.item?.def ?? p.consumable, rar: p.item?.rarity });
  }
  const it: InterView[] = [];
  for (const i of world.interactables.values()) {
    const dx = i.x - cx;
    const dy = i.y - cy;
    if (dx * dx + dy * dy > r2) continue;
    it.push({ id: i.id, x: Math.round(i.x), y: Math.round(i.y), ty: i.type, used: i.used });
  }
  const snap: Snapshot = { tick: world.tick, u: units, gone, z, pk, it };
  if (viewer) snap.me = selfView(world, viewer);
  return snap;
}

function skillView(_world: World, s: { id: string; level: number; aspects: string[]; auto?: boolean; cd: number }): SkillView {
  const def = SKILLS[s.id];
  const mul = def ? resolveSkill(def, s.aspects).cooldownMul : 1;
  const total = Math.max(1, (def?.cooldown ?? 5) * mul * 20);
  const v: SkillView = { id: s.id, level: s.level, aspects: s.aspects, cd: Math.min(1, s.cd / total) };
  if (s.auto) v.auto = true;
  return v;
}

export function selfView(world: World, p: PlayerEnt): SelfView {
  const maxDash = Math.max(1, Math.floor(p.stats.dashCharges));
  return {
    id: p.id,
    hero: p.heroId,
    x: Math.round(p.x * 100) / 100,
    y: Math.round(p.y * 100) / 100,
    seq: p.lastSeq,
    hp: Math.round(p.hp),
    mhp: Math.round(p.maxHp),
    sh: Math.round(p.shield),
    xp: p.xp,
    xpNext: p.xpNext,
    level: p.level,
    power: Math.round(p.power * 10) / 10,
    rank: p.rank,
    rankXp: p.rankXp,
    rankXpNext: p.rankXpNext,
    pts: freePoints(p),
    actives: p.actives.map((s) => skillView(world, s)),
    autos: p.autos.map((s) => skillView(world, s)),
    traits: p.traits.map((t) => ({ id: t.id, level: t.level })),
    keystones: [...p.keystones],
    bless: [...p.blessings],
    eq: p.equipment,
    bp: p.backpack,
    cons: p.consumables,
    draft: p.draft,
    dash: { charges: p.dashCharges, max: maxDash, cd: p.dashCd > 0 ? p.dashCd / Math.max(1, 3 * p.stats.dashCooldown * 20) : 0 },
    st: p.statuses.map((s) => ({ id: s.id, stacks: s.stacks, left: Math.max(0, (s.until - world.tick) / 20) })),
    kills: p.kills,
    fame: p.fameEarned,
    dead: p.dead,
    stats: {
      armor: Math.round(p.stats.armor),
      dmg: Math.round(p.stats.damage * 100) / 100,
      spd: Math.round(p.stats.moveSpeed),
      crit: Math.round(p.stats.critChance * 100) / 100,
      aspd: Math.round(p.stats.attackSpeed * 100) / 100,
      cdr: Math.round(p.stats.cooldown * 100) / 100,
      area: Math.round(p.stats.area * 100) / 100,
      thorns: Math.round(p.stats.thorns * 100) / 100,
      ls: Math.round(p.stats.lifesteal * 100) / 100,
      luck: Math.round(p.stats.luck),
      regen: Math.round(p.stats.hpRegen * 10) / 10,
    },
    flags: [...p.flags],
    pet: p.pet ? { id: p.pet.id, level: p.pet.level } : undefined,
  };
}
